import { useState, useRef, useCallback } from "react";
import { api } from "@/lib/api";
import { Mic, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Hold-to-record mic button.
 * - mousedown/touchstart → börja spela in
 * - mouseup/touchend     → stoppa, transkribera, anropa `onTranscript(text)`
 * - Visar pulserande röd indikator under inspelning.
 */
export function MicButton({ onTranscript, disabled = false }) {
  const [state, setState] = useState("idle"); // idle | recording | uploading
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const handleStop = useCallback(async () => {
    setState("uploading");
    try {
      const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || "audio/webm" });
      // Stäng mikrofon
      streamRef.current?.getTracks().forEach((t) => t.stop());
      streamRef.current = null;

      if (blob.size < 800) {
        toast.error("För kort inspelning");
        setState("idle");
        return;
      }

      const ext = blob.type.includes("mp4") ? "m4a" : "webm";
      const form = new FormData();
      form.append("file", blob, `voice.${ext}`);

      const url = "/jarvis/voice/transcribe?auto_route=true";
      const { data } = await api.post(url, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (!data.ok) {
        toast.error("Transkription misslyckades: " + (data.error || "okänt fel"));
        setState("idle");
        return;
      }
      const text = (data.transcript || "").trim();
      if (!text) {
        toast.error("Hörde inget att skriva ner");
        setState("idle");
        return;
      }
      onTranscript?.(text, data);
    } catch (err) {
      toast.error("Fel vid transkription: " + (err?.response?.data?.detail || err.message));
    } finally {
      setState("idle");
    }
  }, [onTranscript]);

  const start = useCallback(async (e) => {
    e?.preventDefault?.();
    if (state !== "idle" || disabled) return;
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Mikrofon stöds inte i den här webbläsaren");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      // Föredra webm/opus — Whisper accepterar det
      let mime = "audio/webm;codecs=opus";
      if (!MediaRecorder.isTypeSupported(mime)) {
        mime = MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "";
      }
      const recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (ev) => {
        if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
      };
      recorder.onstop = handleStop;
      recorder.start();
      recorderRef.current = recorder;
      setState("recording");
    } catch (err) {
      toast.error("Kunde inte starta mikrofon: " + (err.message || "okänt fel"));
    }
  }, [state, disabled, handleStop]);

  const stop = useCallback((e) => {
    e?.preventDefault?.();
    const r = recorderRef.current;
    if (r && r.state !== "inactive") {
      r.stop();
    }
    recorderRef.current = null;
  }, []);

  const cancelIfLeave = useCallback(() => {
    // Om användaren drar bort fingret/musen utanför knappen, stoppa ändå.
    if (state === "recording") stop();
  }, [state, stop]);

  const recording = state === "recording";
  const uploading = state === "uploading";
  const Icon = uploading ? Loader2 : Mic;

  return (
    <button
      type="button"
      disabled={disabled || uploading}
      onMouseDown={start}
      onMouseUp={stop}
      onMouseLeave={cancelIfLeave}
      onTouchStart={start}
      onTouchEnd={stop}
      onTouchCancel={cancelIfLeave}
      className={`rounded-xl flex-shrink-0 w-10 h-10 flex items-center justify-center transition-all select-none touch-none ${
        recording
          ? "bg-[#DC2626] text-white animate-pulse scale-110 ring-4 ring-[#DC2626]/30"
          : uploading
          ? "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)]"
          : "bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] hover:bg-[#E8E2D6] active:scale-95"
      }`}
      title={recording ? "Släpp för att skicka" : "Håll inne och tala"}
      data-testid="mic-button"
      aria-label="Spela in röstmeddelande"
    >
      <Icon size={16} className={uploading ? "animate-spin" : ""} />
    </button>
  );
}
